import classNames from "classnames";
import * as React from "react";

import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import { Autocomplete } from "../Autocomplete";
import { Tag } from "../Tag";

import styles from "./TagInput.module.css";

/** Control density, on the same scale as `TextInput`, `Select` and `Combobox`. */
export type TagInputSize = "sm" | "md" | "lg";

const sizeClass: Record<TagInputSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

// Trailing debounce applied to the draft before an async suggestion source is
// re-invoked, so a burst of typing collapses to one request. Matches the
// filter pickers' server-search debounce.
const SUGGESTION_DEBOUNCE_MS = 300;

/**
 * Where the draft's completions come from: a fixed list matched client-side by
 * case-insensitive substring, or a fetch keyed on the (debounced) draft text.
 */
export type TagSuggestionsResolver = (
  query: string,
  signal: AbortSignal,
) => Promise<readonly string[]>;

export type TagSuggestions = readonly string[] | TagSuggestionsResolver;

/**
 * One row of the popup: a suggestion, or the trailing "use what I typed" row.
 * `custom` marks the latter, which carries the same text as a suggestion might
 * — the flag, not the value, is what keeps the two rows distinct.
 */
interface SuggestionItem {
  value: string;
  custom: boolean;
}

interface SuggestionState {
  items: readonly string[];
  isLoading: boolean;
  isError: boolean;
}

const NO_SUGGESTIONS: SuggestionState = {
  items: [],
  isLoading: false,
  isError: false,
};

/**
 * Asks a resolver for the debounced draft, holding the latest answer. Each new
 * query aborts the one before it, so a slow early page can't land on top of a
 * later one. Fetched with a plain effect rather than a query: results are
 * ephemeral, so a cache would buy nothing, and the resolver is read through a
 * ref so an inline arrow doesn't refetch every render.
 */
function useResolvedSuggestions(
  resolve: TagSuggestionsResolver | undefined,
  draft: string,
): SuggestionState {
  const resolverRef = React.useRef(resolve);
  resolverRef.current = resolve;
  const enabled = resolve !== undefined;

  const [query, setQuery] = React.useState("");
  const debounceQuery = useDebouncedCallback(setQuery, SUGGESTION_DEBOUNCE_MS);
  React.useEffect(() => {
    if (enabled) {
      debounceQuery(draft.trim());
    }
  }, [draft, enabled, debounceQuery]);

  const [resolved, setResolved] = React.useState(NO_SUGGESTIONS);
  // The empty query is asked too: it is the "show me everything" page a
  // freshly focused field opens with.
  React.useEffect(() => {
    const currentResolve = resolverRef.current;
    if (currentResolve === undefined) {
      setResolved(NO_SUGGESTIONS);
      return;
    }
    const controller = new AbortController();
    setResolved((previous) => ({
      ...previous,
      isLoading: true,
      isError: false,
    }));
    currentResolve(query, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) {
          setResolved({ items, isLoading: false, isError: false });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResolved({ items: [], isLoading: false, isError: true });
        }
      });
    return () => controller.abort();
  }, [query, enabled]);

  return resolved;
}

/** The entries of a fixed list containing the draft, case-insensitively. */
function matchStatic(list: readonly string[], draft: string): SuggestionState {
  const needle = draft.trim().toLowerCase();
  return {
    items: list.filter((option) => option.toLowerCase().includes(needle)),
    isLoading: false,
    isError: false,
  };
}

/** The suggestions currently on offer for `draft`, in either source's form. */
function useSuggestions(
  suggestions: TagSuggestions | undefined,
  draft: string,
): SuggestionState {
  const resolve = typeof suggestions === "function" ? suggestions : undefined;
  const resolved = useResolvedSuggestions(resolve, draft);
  const list = typeof suggestions === "function" ? undefined : suggestions;
  return React.useMemo(
    () => (list === undefined ? resolved : matchStatic(list, draft)),
    [list, resolved, draft],
  );
}

/**
 * The popup's rows: every suggestion not already committed, then the draft
 * itself. The draft row is for text nothing offers — an exact suggestion IS
 * the draft, so it would be the same commit twice over.
 */
function buildRows(
  suggested: readonly string[],
  tags: readonly string[],
  draft: string,
): SuggestionItem[] {
  const rows: SuggestionItem[] = suggested
    .filter((option) => !tags.includes(option))
    .map((option) => ({ value: option, custom: false }));
  if (draft !== "" && !tags.includes(draft) && !suggested.includes(draft)) {
    rows.push({ value: draft, custom: true });
  }
  return rows;
}

/**
 * A native input's props, less those the draft owns: its text, and `size`,
 * which is the field's density rather than a width in characters.
 */
type DraftInputAttributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "defaultValue" | "size"
>;

export interface TagDraftInputProps extends DraftInputAttributes {
  /**
   * The tags already committed: never offered as suggestions, and the trailing
   * "use what I typed" row is left out when the draft is one of them.
   */
  tags: readonly string[];
  /**
   * Fired with each tag the draft commits: a picked suggestion verbatim, or
   * the typed text (trimmed, without a trailing comma) on Enter, on comma, and
   * when focus leaves the field. Typing one of `tags` again commits it too; the
   * caller decides what a repeat means.
   */
  onCommit: (tag: string) => void;
  /** Backspace on an empty draft, for a field that removes its last tag with it. */
  onBackspaceEmpty?: () => void;
  /**
   * Focus left the draft, its field and its popup, after the typed text was
   * committed. For a draft that is shown only while a tag is being added.
   */
  onLeave?: () => void;
  /**
   * Escape in the draft. Fired whether or not the popup was open, and the key
   * goes no further, for a draft that closes on Escape.
   */
  onEscape?: () => void;
  /** See `TagInput`'s `suggestions`. Absent: a plain draft with no popup. */
  suggestions?: TagSuggestions;
  /** See `TagInput`'s `openOnFocus`. @default true */
  openOnFocus?: boolean;
  /**
   * What the field draws around the draft, before it: a tag field's pills.
   * Given, the draft and its children sit in a `role="group"` box, drawn with
   * `groupClassName`, that the popup spans and that a press anywhere in
   * focuses the draft. Left out, the draft stands alone, the width of itself.
   */
  children?: React.ReactNode;
  /** Draws the box around the draft and its children. */
  groupClassName?: string;
  /** Names the box. @default "Tags" */
  groupLabel?: string;
  /** Extra class(es) on the element wrapping the field and its popup. */
  rootClassName?: string;
}

/**
 * The text input a tag is typed into, with its suggestions and its commit
 * rule. Enter, comma and focus leaving the field each commit the typed text,
 * so a tag typed and then left is never lost; Enter on a highlighted row picks
 * that row instead. Backspace on an empty draft is reported for a field to
 * remove its last tag.
 *
 * `TagInput` is this draft with the committed tags as pills in a box around
 * it. A record page shows the draft alone, after its own pills.
 */
export const TagDraftInput = React.forwardRef<
  HTMLInputElement,
  TagDraftInputProps
>(function TagDraftInput(
  {
    tags,
    onCommit,
    onBackspaceEmpty,
    onLeave,
    onEscape,
    suggestions,
    openOnFocus = true,
    children,
    groupClassName,
    groupLabel = "Tags",
    rootClassName,
    className,
    onKeyDown,
    onBlur,
    ...rest
  },
  ref,
) {
  const [draft, setDraft] = React.useState("");
  // The highlighted row, read synchronously inside the Enter handler: Enter
  // reaches the popup too, so with a row highlighted that row's `onClick`
  // commits and the free-text branch must stand down.
  const highlighted = React.useRef<SuggestionItem | undefined>(undefined);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const fieldRef = React.useRef<HTMLDivElement>(null);
  // Owned here so the popup can be held shut while `hasPopupContent` is
  // false; the root's own opens and closes, focus opening it included, are
  // mirrored in.
  const [open, setOpen] = React.useState(false);

  const {
    items: suggested,
    isLoading,
    isError,
  } = useSuggestions(suggestions, draft);

  const trimmedDraft = draft.trim();
  const items = React.useMemo(
    () => buildRows(suggested, tags, trimmedDraft),
    [suggested, tags, trimmedDraft],
  );
  const hasPopup = suggestions !== undefined;
  const hasPopupContent = isLoading || isError || items.length > 0;

  // Suggestions commit verbatim: they are names that already exist, so
  // trimming or stripping a delimiter would target a different tag.
  const commitTag = (tag: string) => {
    setDraft("");
    highlighted.current = undefined;
    if (tag) {
      onCommit(tag);
    }
  };

  // The draft is typed text, so it is tidied first: the comma that
  // committed it, and surrounding whitespace, are delimiters, not name.
  const commitDraft = () => {
    commitTag(draft.trim().replace(/,$/, ""));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.key === "Enter") {
      if (highlighted.current !== undefined) {
        return;
      }
      event.preventDefault();
      commitDraft();
    } else if (event.key === ",") {
      event.preventDefault();
      commitDraft();
    } else if (event.key === "Backspace" && !draft) {
      onBackspaceEmpty?.();
    } else if (event.key === "Escape" && onEscape) {
      event.preventDefault();
      event.stopPropagation();
      onEscape();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    const next = event.relatedTarget as Node | null;
    if (popupRef.current?.contains(next)) {
      return;
    }
    // Cleared here as well as by the root: while `hasPopupContent` holds the
    // popup shut, the root has no open popup to close and reports nothing,
    // so `open` would stay set, and a row arriving after focus left (a
    // removed tag offered again) would open the popup on an unfocused field.
    setOpen(false);
    // Focus still inside the field (a pill's remove button) keeps the draft.
    if (fieldRef.current?.contains(next)) {
      return;
    }
    commitDraft();
    onLeave?.();
  };

  const grouped = children !== undefined;
  // Inside the box the draft fills the room after the pills; standing alone
  // it is whatever its caller's class makes it.
  const inputClassName = classNames({ [styles.draft]: grouped }, className);

  const draftInput = !hasPopup ? (
    <input
      {...rest}
      ref={ref}
      className={inputClassName}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  ) : (
    <Autocomplete.Input
      variant="bare"
      {...rest}
      ref={ref}
      className={inputClassName}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );

  if (!hasPopup) {
    return grouped ? (
      <div
        ref={fieldRef}
        className={groupClassName}
        role="group"
        aria-label={groupLabel}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.currentTarget as HTMLElement).querySelector("input")?.focus();
          }
        }}
      >
        {children}
        {draftInput}
      </div>
    ) : (
      draftInput
    );
  }

  return (
    <Autocomplete
      items={items}
      value={draft}
      // Held shut while there is nothing to show, so a focused field with
      // nothing to suggest doesn't open an empty box.
      open={open && hasPopupContent}
      onOpenChange={setOpen}
      openOnFocus={openOnFocus}
      // The rows are assembled here — committed tags dropped, the draft row
      // appended — so they must be listed verbatim.
      filter={null}
      itemToStringValue={(item: SuggestionItem) => item.value}
      onValueChange={(next, details) => {
        // `item-press` is the picked row's text being written back into the
        // input; the row's `onClick` already committed and cleared it.
        if (details.reason === "item-press") {
          return;
        }
        setDraft(next);
      }}
      onItemHighlighted={(item) => {
        highlighted.current = item;
      }}
      className={rootClassName}
    >
      {grouped ? (
        <Autocomplete.InputGroup
          ref={fieldRef}
          className={groupClassName}
          aria-label={groupLabel}
        >
          {children}
          {draftInput}
        </Autocomplete.InputGroup>
      ) : (
        draftInput
      )}
      <Autocomplete.Content ref={popupRef} busy={isLoading}>
        <Autocomplete.Status>
          {isError ? "Suggestions are unavailable" : null}
        </Autocomplete.Status>
        {isLoading && (
          <div
            className={styles.skeleton}
            data-testid="tag-input-loading"
            role="presentation"
          >
            <span className={styles.skeletonRow} />
            <span className={styles.skeletonRow} />
          </div>
        )}
        <Autocomplete.List hidden={isLoading || undefined}>
          {(item: SuggestionItem) => (
            <Autocomplete.Item
              key={item.custom ? `use-draft:${item.value}` : item.value}
              value={item}
              onClick={() => commitTag(item.value)}
            >
              {item.custom ? (
                <span data-testid="tag-input-use-draft">
                  <Autocomplete.UseTyped text={item.value} />
                </span>
              ) : (
                <span data-testid="tag-input-suggestion">{item.value}</span>
              )}
            </Autocomplete.Item>
          )}
        </Autocomplete.List>
      </Autocomplete.Content>
    </Autocomplete>
  );
});

export interface TagInputProps extends DraftInputAttributes {
  /** The committed tags. Controlled: the field renders what it is given. */
  tags: string[];
  /** Called with the full updated tag list whenever a tag is added or removed. */
  onChange: (tags: string[]) => void;
  /**
   * Tags being saved, for a field that saves each change as it is made: each
   * shows dimmed with a spinner until it is left out again.
   */
  busyTags?: readonly string[];
  /**
   * Placeholder shown in the draft input when there are no tags yet.
   * @default "Add tags…"
   */
  placeholder?: string;
  /**
   * Minimum height, padding, and font size, so a tag field lines up with the
   * other fields of its size.
   * @default "md"
   */
  size?: TagInputSize;
  /**
   * Offer completions for the draft. Absent: a plain draft field, with no
   * popup at all.
   *
   * A function is called with the debounced draft and an `AbortSignal`
   * (aborted when the draft moves on or the field unmounts); it returns
   * already-matching suggestions, rendered verbatim. A static list is matched
   * client-side by case-insensitive substring. Tags already committed are
   * never offered, and a trailing row always offers the draft itself, so text
   * nothing suggests is still one Enter away.
   */
  suggestions?: TagSuggestions;
  /**
   * Whether focusing the draft, by Tab or from code, opens the suggestions.
   * When false they open only when the draft is pressed or typed in, or on
   * ArrowDown, so a form that moves focus to the field leaves the page
   * around it in view.
   * @default true
   */
  openOnFocus?: boolean;
}

/**
 * A tag/chip input primitive. Renders committed tags as removable pills plus
 * a draft text field for adding new ones — Enter, comma or leaving the field
 * commits the draft, Backspace on an empty draft removes the last tag. A pill
 * keeps its tag on one line, ending in an ellipsis when the tag is wider than
 * the field, with the whole tag as its title.
 *
 * Controlled: render the tags you are given and take updates from `onChange`.
 *
 * With `suggestions`, the draft field also offers completions in a popup
 * whose last row commits the draft verbatim. Nothing is highlighted by
 * default, so Enter commits exactly what was typed unless the user moved onto
 * a row first.
 *
 * The forwarded ref attaches to the draft `<input>`, and any additional
 * native input props (`id`, `aria-label`, `aria-describedby`, `disabled`,
 * `name`, etc.) are passed through.
 *
 * @example
 * ```tsx
 * <TagInput tags={tags} onChange={setTags} suggestions={searchTags} />
 * ```
 */
export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  function TagInput(
    {
      tags,
      onChange,
      busyTags,
      placeholder = "Add tags…",
      size = "md",
      suggestions,
      openOnFocus = true,
      disabled,
      ...rest
    },
    ref,
  ) {
    const [announcement, setAnnouncement] = React.useState("");

    const addTag = (tag: string) => {
      if (tags.includes(tag)) {
        setAnnouncement(`"${tag}" is already added`);
        return;
      }
      onChange([...tags, tag]);
      setAnnouncement(`Added "${tag}"`);
    };

    const removeTag = (tag: string) => {
      onChange(tags.filter((t) => t !== tag));
      setAnnouncement(`Removed "${tag}"`);
    };

    const isEmpty = tags.length === 0;
    return (
      <TagDraftInput
        {...rest}
        ref={ref}
        tags={tags}
        onCommit={addTag}
        onBackspaceEmpty={() => {
          if (tags.length) {
            removeTag(tags[tags.length - 1]);
          }
        }}
        suggestions={suggestions}
        openOnFocus={openOnFocus}
        disabled={disabled}
        placeholder={isEmpty ? placeholder : ""}
        groupClassName={classNames(styles.tagInput, sizeClass[size])}
      >
        <ul className={styles.pillList}>
          {tags.map((t) => (
            <li key={t} className={styles.pill}>
              <Tag
                label={t}
                // A disabled field keeps its tags as they are.
                onDelete={disabled ? undefined : () => removeTag(t)}
                busy={busyTags?.includes(t)}
                // Compact pills, so a small or medium field stays no taller
                // than the text fields beside it.
                size={size === "lg" ? "md" : "compact"}
              />
            </li>
          ))}
        </ul>
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </TagDraftInput>
    );
  },
);
